import { useState, type JSX, type ReactNode } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLayout } from '@/hooks/useLayout';
import { useGuardedNavigation } from '@/hooks/useGuardedNavigation';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { SidebarNavLabel } from './SidebarNavLabel';

/** One dot-bulleted child row under a section parent. */
export interface SidebarNavSectionItem {
  /** Visible label, also the link's accessible name. */
  label: string;
  /**
   * Route the child navigates to. Must be a plain absolute pathname (no
   * search/hash, no percent-encodable characters) — the pill hand-off check
   * compares it verbatim against `useLocation().pathname`.
   */
  to: string;
  /**
   * Extra pathnames (exact or a prefix of) that keep THIS child highlighted even
   * though it doesn't navigate there. Used for a sub-page that has no sidebar
   * entry of its own but belongs to this child — e.g. the AI Agent Instructions
   * "Manage catalog" page (KIR-4184), reached from the AI Agent Instructions
   * sub-page. Without it the parent grabs the pill on that route.
   */
  activePaths?: readonly string[];
}

interface SidebarNavSectionProps {
  /** Parent row label (e.g. "Policies"). */
  label: string;
  /** Route the parent row navigates to (usually the section root). */
  to: string;
  /** Icon for the parent row. */
  icon: LucideIcon;
  /** Child sub-pages, in display order. Filter before passing to gate one. */
  items: readonly SidebarNavSectionItem[];
  /** Optional trailing parent-row adornment (e.g. a "NEW" badge). */
  badge?: ReactNode;
  /**
   * Extra route prefixes that keep the parent row highlighted. `NavLink` only
   * prefix-matches `to`, so a section whose children live on sibling top-level
   * routes (e.g. AI AppSec, KIR-3925) passes each child's path here.
   */
  activePaths?: readonly string[];
}

/**
 * Mirror NavLink's `end`-matching (exact pathname, case-insensitive), plus any
 * `activePaths` the child claims (exact or prefix), so the parent hands the pill
 * off exactly when the child NavLink will render it. A mismatch either loses the
 * highlight entirely or double-highlights.
 */
function isChildLinkActive(
  item: SidebarNavSectionItem,
  pathname: string
): boolean {
  const current = pathname.toLowerCase();
  if (item.to.toLowerCase() === current) return true;
  return (item.activePaths ?? []).some(path => {
    const p = path.toLowerCase();
    return current === p || current.startsWith(`${p}/`);
  });
}

/**
 * NavLink-style prefix match on the parent's own route. Keeps the section
 * expanded on section sub-routes that have no sidebar entry (e.g. the policies
 * catalog-management pages), not just on the listed children.
 */
function isPathWithinSection(to: string, pathname: string): boolean {
  const sectionPath = to.toLowerCase();
  const currentPath = pathname.toLowerCase();
  return (
    currentPath === sectionPath || currentPath.startsWith(`${sectionPath}/`)
  );
}

const PARENT_CLASS =
  'flex items-center min-w-0 rounded-lg gap-3 px-3 py-2 w-full box-border text-sm font-semibold transition-all border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function childClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-start gap-2 rounded-lg border py-1.5 pl-9 pr-3 text-sm transition-colors',
    isActive
      ? 'bg-accent font-medium text-accent-foreground border-accent-border'
      : 'text-muted-foreground border-transparent hover:text-accent-hover-foreground'
  );
}

/**
 * SidebarNavSection — a collapsible sidebar section: a highlighted parent row
 * (icon + label) over dot-bulleted, indented child sub-pages.
 *
 * KIR-4302: children are collapsed by default and expand inline (no flyout)
 * when the section owns the current route. The chevron on the right of the
 * parent row is a separate button that toggles without navigating; clicking the
 * row itself both expands the section and navigates to `to`. Sections the user
 * opened stay open as they navigate — clicking into one section never folds the
 * others shut; only the chevron closes a section again, and that dismissal
 * expires on the next navigation so the section can still auto-open for its own
 * pages. Nothing is persisted across reloads. The icon-only rail
 * (`sidebarCollapsed`) shows neither children nor the chevron.
 *
 * The selected sub-page (each child is `end`-matched) carries the blue pill
 * highlight while the active parent only switches to full-contrast text
 * (KIR-4159). The parent keeps the pill when it can't hand it off to a child:
 * when the sidebar is collapsed (children hidden), when the section itself is
 * collapsed, or on a section sub-route that has no sidebar entry (e.g. the
 * policies catalog-management pages).
 * Navigation goes through {@link useGuardedNavigation} so unsaved-work guards
 * run before the route changes.
 *
 * Backs the Policies, Shadow AI, and Agents Activity groups; pass a filtered
 * `items` list to gate a child behind a feature flag.
 */
export function SidebarNavSection({
  label,
  to,
  icon: Icon,
  items,
  badge,
  activePaths,
}: SidebarNavSectionProps): JSX.Element {
  const navigate = useGuardedNavigation();
  const { sidebarCollapsed } = useLayout();
  const { pathname, key: locationKey } = useLocation();
  const anyChildActive = items.some(item => isChildLinkActive(item, pathname));
  // KIR-3925: sections whose children live on sibling top-level routes (AI
  // AppSec) aren't covered by the parent NavLink's prefix match — activePaths
  // marks the section active on those routes too.
  const forceParentActive =
    activePaths?.some(
      path => pathname === path || pathname.startsWith(`${path}/`)
    ) ?? false;

  // KIR-4302: collapsed by default; expanded whenever the section owns the
  // current route, plus a manual override. The two override directions expire
  // differently, which is the whole model:
  //
  //   - An **expand** sticks. Navigating to another section no longer folds this
  //     one shut, so the sections the user opened stay open while they click
  //     around. Only the chevron closes them again.
  //   - A **collapse** holds only on the history entry (`location.key`) it was
  //     made on. Otherwise one dismissal would outlive the route it was about
  //     and the section could never auto-open for its own pages again.
  //
  // `location.key` — not `pathname` — dates the collapse: it is unique per
  // navigation, so the override expires on the next one instead of lying
  // dormant and re-applying on every later visit to that same path.
  //
  // Storing the override as one value keeps `open` a pure derivation: no effect
  // to sync (which would show the stale state for a frame), no setState in
  // render.
  const routeOpen =
    anyChildActive || forceParentActive || isPathWithinSection(to, pathname);
  const [override, setOverride] = useState<{
    locationKey: string;
    open: boolean;
  } | null>(null);
  const overrideApplies =
    override !== null &&
    (override.open || override.locationKey === locationKey);
  const open = overrideApplies ? override.open : routeOpen;
  const setOpen = (next: boolean): void =>
    setOverride({ locationKey, open: next });
  // The icon-only rail has no room for children or a toggle (unchanged).
  const showChevron = !sidebarCollapsed && items.length > 0;
  // Whether a child row is on screen to receive the pill. `anyChildActive` alone
  // isn't enough post-KIR-4302: collapsing the section unmounts the matching
  // child, so the parent has to take the pill back or the highlight disappears
  // entirely while the route is still inside the section.
  const childCanShowPill = !sidebarCollapsed && open && anyChildActive;

  // Clicking a sub-item latches the section open as well as navigating, so a
  // section reached by deep link (no click on the parent row) also stays open
  // once the user leaves it — same promise as a section they expanded by hand.
  const handleNavigate =
    (target: string) =>
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
      e.preventDefault();
      setOpen(true);
      navigate(target);
    };

  // Clicking the parent expands the section as well as navigating — and, because
  // an expand sticks, keeps it open after the user moves on. The override also
  // covers the case where no navigation lands at all (the unsaved-work guard
  // blocked it while the section was manually collapsed).
  const handleParentClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setOpen(true);
    navigate(to);
  };

  return (
    <Collapsible
      open={!sidebarCollapsed && open}
      onOpenChange={setOpen}
      className="flex flex-col gap-1"
    >
      <div className="relative flex items-center">
        <NavLink
          to={to}
          onClick={handleParentClick}
          className={({ isActive }) => {
            const sectionActive = isActive || forceParentActive;
            // The parent hands the pill off to the matching child; it keeps the
            // pill whenever no child can show it (rail mode, a collapsed
            // section, or an unlisted sub-route of the section).
            const showPill = sectionActive && !childCanShowPill;
            return cn(
              PARENT_CLASS,
              sidebarCollapsed ? 'w-fit gap-0' : '',
              // Reserve the chevron gutter (PARENT_CLASS sets px-3).
              showChevron ? 'pr-9' : '',
              showPill
                ? 'bg-accent text-accent-foreground border-accent-border hover:bg-accent hover:text-accent-foreground'
                : cn(
                    'border-transparent hover:bg-accent-hover hover:text-accent-hover-foreground',
                    sectionActive ? 'text-foreground' : 'text-muted-foreground'
                  )
            );
          }}
        >
          <Icon className="h-4 w-4 flex-shrink-0" />
          <SidebarNavLabel label={label} collapsed={sidebarCollapsed} />
          {!sidebarCollapsed && badge}
        </NavLink>

        {/* Sibling of the anchor, not a child: a button may not nest inside an
            <a>, and a chevron inside it would change the link's accessible
            name (which unit and e2e locators match exactly). Absolute
            positioning keeps the single-row look and the full-width pill. */}
        {showChevron && (
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={`Toggle ${label} submenu`}
              onClick={e => e.stopPropagation()}
              className="absolute right-1 top-1/2 z-10 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'h-4 w-4 transition-transform duration-200',
                  open && 'rotate-180'
                )}
              />
            </button>
          </CollapsibleTrigger>
        )}
      </div>

      {/* Radix animates height on the content element, so the gap lives on an
          inner wrapper — otherwise the animation jumps. */}
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="flex flex-col gap-1">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={handleNavigate(item.to)}
              // Highlight follows isChildLinkActive (to + activePaths), not
              // NavLink's own `end` match, so a child stays lit on its unlisted
              // sub-pages (e.g. the catalog page, KIR-4184).
              className={childClassName({
                isActive: isChildLinkActive(item, pathname),
              })}
            >
              {/* Dot aligned to the first text line (text-sm line-height 20px,
                  dot 6px → 7px offset) so long labels wrap instead of truncate. */}
              <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/50" />
              <span className="min-w-0 break-words">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
